// src/context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import axiosInstance, { setCsrfToken } from "../api/axiosConfig";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    token: null,
    role: null,
    userId: null,
  });

  const [loading, setLoading] = useState(true); // Nuevo estado de carga

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem("token");
      console.log("Initializing auth. Token found:", token);
      if (token) {
        try {
          const decoded = jwtDecode(token);
          console.log("Decoded token:", decoded);
          setAuthState({
            token,
            role: decoded.role,
            userId: decoded.userId,
          });

          // Obtener CSRF token
          const csrfRes = await axiosInstance.get("/csrf-token");
          console.log("CSRF Token fetched:", csrfRes.data.csrfToken);
          const csrfToken = csrfRes.data.csrfToken;
          setCsrfToken(csrfToken);
        } catch (error) {
          console.error("Error al inicializar autenticación:", error);
          logout();
        }
      } else {
        console.log("No token found in localStorage.");
      }
      setLoading(false); // Finalizar estado de carga
    };
    initializeAuth();
  }, []);

  const login = async (credentials) => {
    try {
      const { data } = await axiosInstance.post("/auth/login", credentials);
      console.log("Login response:", data);

      localStorage.setItem("token", data.accessToken);
      const decoded = jwtDecode(data.accessToken);
      console.log("Decoded login token:", decoded);

      setAuthState({
        token: data.accessToken,
        role: decoded.role,
        userId: decoded.userId,
      });

      // Obtener CSRF token
      const csrfRes = await axiosInstance.get("/csrf-token");
      console.log("CSRF Token after login:", csrfRes.data.csrfToken);
      const csrfToken = csrfRes.data.csrfToken;
      setCsrfToken(csrfToken);

      return { user: data.user };
    } catch (error) {
      console.error("Error during login:", error);
      logout();
      throw error;
    }
  };

  const logout = () => {
    console.log("Logging out user.");
    localStorage.removeItem("token");
    setAuthState({
      token: null,
      role: null,
      userId: null,
    });
  };

  const refreshTokenFunc = async () => {
    try {
      const { data } = await axiosInstance.post("/auth/refresh");
      console.log("Refresh token response:", data);

      localStorage.setItem("token", data.accessToken);
      const decoded = jwtDecode(data.accessToken);
      console.log("Decoded refresh token:", decoded);

      setAuthState((prev) => ({
        ...prev,
        token: data.accessToken,
        role: decoded.role,
        userId: decoded.userId,
      }));

      // Obtener CSRF token
      const csrfRes = await axiosInstance.get("/csrf-token");
      console.log("CSRF Token after refresh:", csrfRes.data.csrfToken);
      const csrfToken = csrfRes.data.csrfToken;
      setCsrfToken(csrfToken);

      return data.accessToken;
    } catch (error) {
      console.error("Error during token refresh:", error);
      logout();
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        logout,
        refreshToken: refreshTokenFunc,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
